import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trees, ArrowRight, Check, Sun, Leaf, Droplets, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const HERO_IMAGE = "https://images.pexels.com/photos/5554796/pexels-photo-5554796.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONTENT_IMAGE_1 = "https://images.unsplash.com/photo-1762086091989-bd3dc726927d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHx0ZXJyYWNlJTIwYmFsY29ueSUyMGNsZWFuaW5nJTIwb3V0ZG9vcnxlbnwwfHx8fDE3NzQxMTg3NTV8MA&ixlib=rb-4.1.0&q=85";

export const NettoyageTerrassePage = () => {
  const questions = [
    { id: 'surface_terrasse', label: 'Surface de la terrasse ?', type: 'text', placeholder: 'Ex: 30 m²' },
    { id: 'materiau', label: 'Matériau ?', type: 'select', options: ['Bois', 'Dalles béton', 'Carrelage', 'Pierre naturelle', 'Composite', 'Autre'] },
    { id: 'probleme', label: 'Problème principal ?', type: 'select', options: ['Mousse/Lichens', 'Taches noires', 'Verdissement', 'Usure générale', 'Tout'] },
    { id: 'urgence', label: 'Délai souhaité ?', type: 'select', options: ['Urgent', 'Cette semaine', 'Flexible'] }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* PILIER 1: ATTENTION */}
      <section className="hero-section relative min-h-[70vh] flex items-center">
        <img src={HERO_IMAGE} alt="Terrasse moderne propre" className="hero-image-bg" />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Trees className="h-10 w-10 text-white" />
              <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">Homely Nettoyage</Badge>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              Retrouvez une terrasse comme au premier jour
            </h1>
            
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl">
              Mousse, lichens, taches noires : nous redonnons à votre terrasse son éclat d'origine 
              grâce à notre traitement haute pression et anti-mousse longue durée.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Sun className="h-5 w-5 text-yellow-400" />
                <span className="text-white text-sm">Prêt pour l'été</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Leaf className="h-5 w-5 text-green-400" />
                <span className="text-white text-sm">Traitement écologique</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Droplets className="h-5 w-5 text-blue-400" />
                <span className="text-white text-sm">Protection 12 mois</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="btn-premium h-14 px-10 text-base rounded-full"
                onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
              >
                Devis gratuit en 2 min
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2 text-white">
                <span className="text-2xl font-bold">Dès 8 CHF/m²</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILIER 2: ÉMOTION */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Pourquoi votre terrasse devient verte et glissante ?
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                En Suisse romande, l'humidité et les conditions climatiques favorisent la prolifération 
                de mousses, lichens et algues sur vos surfaces extérieures. En plus d'être inesthétiques, 
                ces dépôts rendent votre terrasse <strong>dangereusement glissante</strong>.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { title: 'Risque de chute', desc: 'Une terrasse mouillée et couverte de mousse peut être très glissante' },
                  { title: 'Dégradation du matériau', desc: 'Les racines des mousses abîment durablement bois, pierre et béton' },
                  { title: 'Dévalorisation du bien', desc: 'Une terrasse négligée fait perdre de la valeur à votre propriété' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-orange-50 rounded-lg border border-orange-100">
                    <span className="text-orange-500 font-bold text-xl">!</span>
                    <div>
                      <p className="font-semibold text-orange-800">{item.title}</p>
                      <p className="text-sm text-orange-700">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img src={CONTENT_IMAGE_1} alt="Terrasse rooftop propre" className="rounded-2xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* PILIER 3: MÉMOIRE - Méthode */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Notre méthode</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Un traitement complet en 3 étapes
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: '01', title: 'Nettoyage haute pression', desc: 'Élimination de la mousse, lichens et saletés incrustées avec pression adaptée au matériau' },
              { step: '02', title: 'Traitement anti-mousse', desc: 'Application d\'un produit biocide qui empêche la repousse pendant 12 mois minimum' },
              { step: '03', title: 'Protection', desc: 'Selon le matériau : huile pour bois, hydrofuge pour pierre ou béton' }
            ].map((item, idx) => (
              <div key={idx} className="card-premium p-8 text-center">
                <div className="text-4xl font-bold mb-4" style={{ color: 'var(--sage)' }}>{item.step}</div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILIER 4: DÉCISION - Preuves sociales */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-2xl mb-12" style={{ backgroundColor: 'var(--sage)' }}>
            {[
              { value: '500+', label: 'Terrasses nettoyées' },
              { value: '12 mois', label: 'Garantie anti-mousse' },
              { value: '4.8/5', label: 'Note clients' },
              { value: '48h', label: 'Délai intervention' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <p className="text-3xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="bg-secondary/30 p-8 rounded-2xl max-w-3xl mx-auto text-center">
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />)}
            </div>
            <p className="text-lg italic mb-4">
              "Ma terrasse en bois était complètement verte. Après le passage de Homely, 
              c'est le jour et la nuit ! Et 6 mois après, toujours impeccable."
            </p>
            <p className="font-semibold">— Marc T., Morges</p>
          </div>
        </div>
      </section>

      {/* PILIER 5: ACTION */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} id="form">
        <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Demandez votre devis gratuit
            </h2>
            <p className="text-white/70">Estimation précise sous 24h</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <ServiceLeadForm service="nettoyage" pole="nettoyage" questions={questions} />
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h3 className="font-semibold mb-4">Autres services de nettoyage</h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/nettoyage/canape" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage canapé</Link>
            <Link to="/nettoyage/vitres" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage vitres</Link>
            <Link to="/nettoyage/fin-de-chantier" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Fin de chantier</Link>
            <Link to="/nettoyage/apres-bail" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Après bail</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
